import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { AuthService } from '@/services/auth';
import { ApiService, PrimeService } from '@/services';
import useUserStore from './user';
import { createLearningObjectStore } from './learningObject';
import useSlideStore from './slide';
import useLeaderboardStore from './leaderboard';
import useResourceStore from './resource';
import useLiveStreamStore from './livestream';
import usePromotionStore from './promotion';
import useMessageStore from './message';
import useConfigStore from './config';

export default defineStore('index', () => {
  const authService = new AuthService();
  const primeService = ref(new PrimeService(authService));
  const apiService = ref(new ApiService(authService));
  const isAuthenticated = computed(() => !!authService.auth);
  const isRootPlayerOpened = ref(false);

  const config = useConfigStore();
  const appBaseDomain = computed(
    () => config.config.NV_IMG_BASE_URL?.replace(/\/$/, '') || config.config.NV_GATEWAY.replace(/\/$/, '')
  );

  const learningObjectMap = new Map<string, ReturnType<typeof createLearningObjectStore>>();
  const defaultLearningObjectStore = createLearningObjectStore('learningObject');

  const learningObjectByKey = (key?: string) => {
    if (!key) {
      return defaultLearningObjectStore;
    }

    if (!learningObjectMap.has(key)) {
      const newLearningObjectStore = createLearningObjectStore(key);
      learningObjectMap.set(key, newLearningObjectStore);
    }

    return learningObjectMap.get(key)!;
  };

  return {
    appBaseDomain,

    user: useUserStore(),
    learningObject: defaultLearningObjectStore,
    learningObjectByKey,
    slide: useSlideStore(),
    leaderboard: useLeaderboardStore(),
    resource: useResourceStore(),
    livestream: useLiveStreamStore(),
    promotion: usePromotionStore(),
    message: useMessageStore(),
    config,

    isAuthenticated,
    isRootPlayerOpened,

    primeService,
    authService,
    apiService,

    async init() {
      await this.config.init();
      await authService.init(this.config.config);
      primeService.value = new PrimeService(authService);
      apiService.value = new ApiService(authService);

      if (authService.auth) {
        await primeService.value.init(authService.auth);
        await apiService.value.init(authService.auth);
        await this.user.init();
        if (this.user.user?.contentLanguage) {
          primeService.value.locale(this.user.user.contentLanguage);
        }
      }
    }
  };
});
